import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Ban } from 'lucide-react';
import { Button, Card, CardBody, Spinner } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useCapturePayPalOrder, type PayPalCaptureResult } from '@/api/billing-payments';
import { publishPaymentResult } from '@/lib/payment-broadcast';
import { trackPurchase } from '@/lib/analytics-events';
import styles from './Billing.module.css';

type Status = 'processing' | 'success' | 'error' | 'cancelled';

/**
 * PayPal return route — PayPal redirects here after the payer approves (or
 * cancels) an order. Reads `token` (the PayPal order id) + `PayerID`, captures
 * the order to finalise the payment, and shows the outcome. Rendered outside
 * AppLayout (opened in a popup/tab from the PaymentDialog).
 */
export default function PayPalCallback() {
  const [params] = useSearchParams();
  const token = params.get('token'); // PayPal order id
  const payerId = params.get('PayerID');

  const [status, setStatus] = useState<Status>('processing');
  const [error, setError] = useState<string | null>(null);
  const [capture, setCapture] = useState<PayPalCaptureResult | null>(null);
  const capturePayPal = useCapturePayPalOrder();
  // PayPal can re-fire the effect; capture the order at most once.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // No order, or approval cancelled (PayPal omits PayerID) → cancelled.
    if (!token || !payerId) {
      setStatus('cancelled');
      return;
    }

    // Await the promise directly rather than the mutate-scoped callbacks:
    // StrictMode unmounts the first mount's mutation observer during cleanup, so
    // callbacks bound to mutate() never fire when the capture resolves, leaving
    // the UI stuck on "processing". The promise settles regardless of observer.
    (async () => {
      try {
        const result = await capturePayPal.mutateAsync({ orderId: token, reference: '' });
        if (result.status === 'COMPLETED') {
          setCapture(result);
          setStatus('success');
          // Tell the tab that opened the PaymentDialog so it can auto-close +
          // refresh — the reference encodes the invoice id (INV{id}-{cust}-{stamp}).
          const invoiceId = result.reference?.match(/^INV(\d+)-/)?.[1];
          if (invoiceId) publishPaymentResult({ invoiceId, status: 'success' });
          trackPurchase(result.orderId, result.amount, 'paypal', result.currency);
        } else {
          setError(`Unexpected payment status: ${result.status}`);
          setStatus('error');
        }
      } catch {
        setError('Failed to process the payment. Please try again.');
        setStatus('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => {
    if (window.opener) window.close();
    else window.location.href = '/account/billing';
  };
  const closeLabel = window.opener ? 'Close window' : 'Back to billing';

  return (
    <div className={styles.callbackScreen}>
      <Card style={{ width: '100%', maxWidth: 420 }}>
        <CardBody>
          {status === 'processing' && (
            <div className={styles.callbackBody}>
              <Spinner />
              <p className={styles.callbackTitle}>Processing your payment…</p>
              <p className={styles.callbackHint}>Please wait while we confirm your PayPal payment.</p>
            </div>
          )}

          {status === 'success' && capture && (
            <div className={styles.callbackBody}>
              <CheckCircle2 size={56} color="var(--vn-success-solid)" />
              <p className={styles.callbackTitle}>Payment successful</p>
              <div className={styles.payInfo} style={{ width: '100%' }}>
                <div className={styles.payInfoRow}>
                  <span>Order</span>
                  <span style={{ fontFamily: 'var(--vn-font-mono)' }}>{capture.orderId}</span>
                </div>
                <div className={styles.payInfoRow}>
                  <span>Transaction</span>
                  <span style={{ fontFamily: 'var(--vn-font-mono)' }}>{capture.captureId}</span>
                </div>
                <div className={styles.payInfoRow}>
                  <span>Amount</span>
                  <span>
                    {capture.amount.toFixed(2)} {capture.currency}
                  </span>
                </div>
                {capture.reference && (
                  <div className={styles.payInfoRow}>
                    <span>Reference</span>
                    <span style={{ fontFamily: 'var(--vn-font-mono)' }}>{capture.reference}</span>
                  </div>
                )}
              </div>
              <Button onClick={close} style={{ width: '100%' }}>
                {closeLabel}
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className={styles.callbackBody}>
              <XCircle size={56} color="var(--vn-danger-solid)" />
              <p className={styles.callbackTitle}>Payment failed</p>
              {error && <Alert tone="danger">{error}</Alert>}
              <Button onClick={close} style={{ width: '100%' }}>
                {closeLabel}
              </Button>
            </div>
          )}

          {status === 'cancelled' && (
            <div className={styles.callbackBody}>
              <Ban size={56} color="var(--vn-warning-solid)" />
              <p className={styles.callbackTitle}>Payment cancelled</p>
              <p className={styles.callbackHint}>You cancelled the PayPal payment. No charge was made.</p>
              <Button variant="secondary" onClick={close} style={{ width: '100%' }}>
                {closeLabel}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
